import React, { useState, useEffect } from 'react';
import { Check, Close } from '@mui/icons-material';
import api, { roleService, timesheetService } from '../services/api';
import { PillButton } from './ui/Buttons';

interface TeamMember {
  userId: number;
  userName: string;
  email: string;
  role: string;
  assignedAt: string;
}

interface Timesheet {
  id: number;
  userId: number;
  userName: string;
  projectId: number;
  projectName: string;
  date: string;
  taskDescription: string;
  hours: number;
  status: string;
  isLocked: boolean;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

const TeamDashboard: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamTimesheets, setTeamTimesheets] = useState<Timesheet[]>([]);
  const [selectedMember, setSelectedMember] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Fetch team members
      const teamData = await roleService.getUserTeam(user.id);
      setTeamMembers(teamData || []);

      // Fetch team timesheets
      const timesheetsData = await timesheetService.getTeamTimesheets();
      setTeamTimesheets(timesheetsData || []);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTimesheet = async (timesheetId: number) => {
    try {
      await timesheetService.approveTimesheet(timesheetId);
      fetchTeamData(); // Refresh data
      alert('Timesheet approved successfully!');
    } catch (error) {
      console.error('Error approving timesheet:', error);
      alert('Error approving timesheet');
    }
  };

  const handleRejectTimesheet = async (timesheetId: number) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;

    try {
      await timesheetService.rejectTimesheet(timesheetId, reason);
      fetchTeamData(); // Refresh data
      alert('Timesheet rejected successfully!');
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
      alert('Error rejecting timesheet');
    }
  };

  const filteredTimesheets = selectedMember 
    ? teamTimesheets.filter(t => t.userId === selectedMember)
    : teamTimesheets;

  const pendingTimesheets = filteredTimesheets.filter(t => t.status === 'Submitted');

  if (loading) {
    return <div>Loading team data...</div>;
  }

  return (
    <div className="team-dashboard">
      <h2>Team Dashboard</h2>
      
      <div className="dashboard-grid">
        <div className="team-members-section">
          <h3>Team Members ({teamMembers.length})</h3>
          <div className="member-filter">
            <select 
              value={selectedMember || ''} 
              onChange={(e) => setSelectedMember(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">All Team Members</option>
              {teamMembers.map(member => (
                <option key={member.userId} value={member.userId}>
                  {member.userName}
                </option>
              ))}
            </select>
          </div>
          <div className="members-list">
            {teamMembers.map(member => (
              <div key={member.userId} className="member-card">
                <h4>{member.userName}</h4>
                <p>{member.email}</p>
                <span className="role-badge">{member.role}</span>
                <small>Assigned: {new Date(member.assignedAt).toLocaleDateString()}</small>
              </div>
            ))}
          </div>
        </div>

        <div className="timesheets-section">
          <h3>
            {selectedMember 
              ? `${teamMembers.find(m => m.userId === selectedMember)?.userName}'s Timesheets` 
              : 'Team Timesheets'
            }
          </h3>
          
          {pendingTimesheets.length > 0 && (
            <div className="pending-approvals">
              <h4>Pending Approvals ({pendingTimesheets.length})</h4>
              {pendingTimesheets.map(timesheet => (
                <div key={timesheet.id} className="timesheet-card pending">
                  <div className="timesheet-header">
                    <span className="user-name">{timesheet.userName}</span>
                    <span className="date">{new Date(timesheet.date).toLocaleDateString()}</span>
                    <span className="hours">{timesheet.hours}h</span>
                  </div>
                  <div className="timesheet-details">
                    <p><strong>Project:</strong> {timesheet.projectName}</p>
                    <p><strong>Task:</strong> {timesheet.taskDescription}</p>
                    <p><strong>Submitted:</strong> {timesheet.submittedAt ? new Date(timesheet.submittedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div className="timesheet-actions">
                    <PillButton variant="success" size="sm" onClick={() => handleApproveTimesheet(timesheet.id)}>
                      <Check sx={{ fontSize: 14 }} /> Approve
                    </PillButton>
                    <PillButton variant="danger" size="sm" onClick={() => handleRejectTimesheet(timesheet.id)}>
                      <Close sx={{ fontSize: 14 }} /> Reject
                    </PillButton>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="all-timesheets">
            <h4>All Timesheets</h4>
            <div className="timesheets-grid">
              {filteredTimesheets.map(timesheet => (
                <div key={timesheet.id} className={`timesheet-card ${timesheet.status.toLowerCase()}`}>
                  <div className="timesheet-header">
                    <span className="user-name">{timesheet.userName}</span>
                    <span className="date">{new Date(timesheet.date).toLocaleDateString()}</span>
                    <span className="hours">{timesheet.hours}h</span>
                    <span className={`status ${timesheet.status.toLowerCase()}`}>
                      {timesheet.status}
                    </span>
                  </div>
                  <div className="timesheet-details">
                    <p><strong>Project:</strong> {timesheet.projectName}</p>
                    <p><strong>Task:</strong> {timesheet.taskDescription}</p>
                    {timesheet.rejectionReason && (
                      <p className="rejection-reason">
                        <strong>Rejection Reason:</strong> {timesheet.rejectionReason}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .team-dashboard {
          padding: 20px;
        }
        .dashboard-grid {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 20px;
          margin-top: 20px;
        }
        .team-members-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 16px;
        }
        .member-filter {
          margin-bottom: 15px;
        }
        .member-filter select {
          width: 100%;
          padding: 8px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
        }
        .member-card {
          background: white;
          padding: 15px;
          margin-bottom: 10px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        .member-card h4 {
          margin: 0 0 5px 0;
          color: #1e293b;
        }
        .member-card p {
          margin: 0 0 8px 0;
          color: #64748b;
          font-size: 0.9em;
        }
        .role-badge {
          background: #7c3aed;
          color: white;
          padding: 2px 8px;
          border-radius: 9999px;
          font-size: 0.8em;
        }
        .member-card small {
          display: block;
          margin-top: 8px;
          color: #94a3b8;
        }
        .timesheets-section {
          background: white;
          padding: 20px;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
        }
        .pending-approvals {
          margin-bottom: 30px;
          padding: 15px;
          background: #fffbeb;
          border-radius: 12px;
          border: 1px solid #fde68a;
        }
        .timesheet-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 10px;
        }
        .timesheet-card.pending {
          border-color: #f59e0b;
          background: #fffbeb;
        }
        .timesheet-card.approved {
          border-color: #059669;
          background: #ecfdf5;
        }
        .timesheet-card.rejected {
          border-color: #e11d48;
          background: #fff1f2;
        }
        .timesheet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .user-name {
          font-weight: bold;
          color: #1e293b;
        }
        .date {
          color: #64748b;
        }
        .hours {
          font-weight: bold;
          color: #7c3aed;
        }
        .status {
          padding: 2px 8px;
          border-radius: 9999px;
          font-size: 0.8em;
          font-weight: bold;
        }
        .status.submitted {
          background: #f59e0b;
          color: #92400e;
        }
        .status.approved {
          background: #059669;
          color: white;
        }
        .status.rejected {
          background: #e11d48;
          color: white;
        }
        .status.draft {
          background: #64748b;
          color: white;
        }
        .timesheet-details p {
          margin: 5px 0;
          color: #64748b;
        }
        .rejection-reason {
          color: #e11d48 !important;
          font-style: italic;
        }
        .timesheet-actions {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }
        .timesheets-grid {
          max-height: 500px;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
};

export default TeamDashboard;